import { useMemo, useState } from "react";

import { twMerge } from "tailwind-merge";

import { type DataTypeProvider } from "@devexpress/dx-react-grid";
import { useDeleteCycle } from "@features/mmm/hooks/use-delete-cycle";
import { CreateMediaPlanTrackedComponent } from "@pages/mmm/amplitude-tracking/components";
import { MMMTab } from "@pages/mmm/amplitude-tracking/mmm-event";
import { useTrackMMMEvent } from "@pages/mmm/amplitude-tracking/use-track-mmm-event";
import {
  PrimaryButton,
  SecondaryButton,
  TertiaryButton,
} from "@shared/buttons";
import { Modal } from "@shared/modal";

import { formatDates } from "../consts";

export const DeleteCycleFormatter = ({
  row,
}: DataTypeProvider.ValueFormatterProps) => {
  const [isConfirmingDelete, setIsConfirmingDelete] = useState(false);
  const [showDeletionErrorModal, setShowDeletionErrorModal] = useState(false);
  const [deletionErrorMessage, setDeletionErrorMessage] = useState("");

  const trackMMMEvent = useTrackMMMEvent(MMMTab.CREATE_MEDIA_PLAN);

  const { deleteCycle, isDeleteCycleLoading } = useDeleteCycle();

  const handleClickDeleteCycle = () => {
    trackMMMEvent({
      component: CreateMediaPlanTrackedComponent.DELETE_MEDIA_PLAN_BUTTON,
      action: "onClick",
      actionInfo: "Open delete cycle modal",
    });
    setIsConfirmingDelete(true);
  };

  const handleDelete = async () => {
    try {
      trackMMMEvent({
        component:
          CreateMediaPlanTrackedComponent.DELETE_MEDIA_PLAN_CONFIRMATION_BUTTON,
        action: "onClick",
        actionInfo: "Confirm delete cycle",
      });
      const cycleId = row.id;
      const wasSuccessful = await deleteCycle(cycleId);
      if (!wasSuccessful) {
        setShowDeletionErrorModal(true);
        setDeletionErrorMessage("You cannot delete your only cycle");
      }
    } catch (err) {
      setShowDeletionErrorModal(true);
      setDeletionErrorMessage("There was an error deleting your cycle");
    } finally {
      setIsConfirmingDelete(false);
    }
  };

  const cycleName = useMemo(() => {
    return row.name || formatDates(row.date_range);
  }, [row.name, row.date_range]);

  return (
    <>
      <div className="flex items-center justify-start">
        <TertiaryButton
          onClick={handleClickDeleteCycle}
          className={twMerge(
            "uppercase text-mars [&_i]:text-mars hover:bg-mars-2",
            "flex items-center justify-center gap-2",
          )}
        >
          <i className="fa-regular fa-trash" />
          Delete
        </TertiaryButton>
      </div>
      <Modal
        isOpen={isConfirmingDelete}
        onClose={() => setIsConfirmingDelete(false)}
        titleContent={
          <span className="text-xl font-semibold text-mars+1">
            You are about to delete a cycle
          </span>
        }
      >
        <div className="flex flex-col justify-start gap-4">
          <span>
            Are you sure you want to delete cycle{" "}
            <span className="font-semibold">{cycleName}</span>?
          </span>
          <div className="flex flex-row justify-between">
            <SecondaryButton onClick={() => setIsConfirmingDelete(false)}>
              Cancel
            </SecondaryButton>
            <PrimaryButton
              className="bg-mars+1"
              onClick={handleDelete}
              loading={isDeleteCycleLoading}
            >
              Delete
            </PrimaryButton>
          </div>
        </div>
      </Modal>
      <Modal
        isOpen={showDeletionErrorModal}
        onClose={() => setShowDeletionErrorModal(false)}
        titleContent={
          <span className="text-xl font-semibold text-mars+1">
            Error deleting cycle
          </span>
        }
      >
        <div className="flex flex-col justify-start gap-4">
          <span>{deletionErrorMessage}</span>
          <div className="flex flex-row justify-between">
            <SecondaryButton onClick={() => setShowDeletionErrorModal(false)}>
              Close
            </SecondaryButton>
          </div>
        </div>
      </Modal>
    </>
  );
};
